<template src="./DataAnalysis.html"></template>

<script>
import { mapState, mapActions } from "vuex";

/*
    Private Libs
*/
import Chart from "@/components/Chart/Chart.vue";
import DataSheet from "@/components/DataAnalysis/DataSheet/DataSheet.vue";

/*
  Private Components
*/
import PanelIconValues from "@/components/Panels/IconValues/PanelIconValues.vue";

// Tabs
import Dashboard from "@/modules/DataAnalysis/Dashboard/Dashboard.vue";
import Risk from "@/modules/DataAnalysis/Risk/Risk.vue";

import MenuIcon from '@/components/Menu/MenuIcon/MenuIcon.vue';
import { HI_EXPOSURE_LEVELS, summarizeExposure } from "@/modules/DataAnalysis/HeatIndex/hiExposure";
import { alignToReferenceYear } from "@/modules/DataAnalysis/compareAxis";
import html2canvas from "html2canvas";
import { annualChartCsvFilename, downloadChartCsv } from "@/modules/DataAnalysis/chartCsv";

export default {
  name: "DataAnalysis",
  components: {
    Chart,
    DataSheet,
    PanelIconValues,
    Dashboard,
    Risk,
    MenuIcon,
  },
  data: () => ({
    loadingCharts: {},
    // Insertion index of the RISK <v-tab>/<v-tab-item> pair in
    // DataAnalysis.html — see onTabChange() and the comment there about the
    // orphaned Table tab-item that must stay at index 4, not 3.
    RISK_TAB_INDEX: 3,

    selected: [],
    dialog: false,
    currentRequestId:0,

    //variableConfig: {},

    // Save the last position to show/hide the refresh data button
    lastPosition: { latitude: null, longitude: null },

    // Disable Load Data Button if there is no modification on input params
    disableLoadData: false,

    /*
     * Vars
     */
    selectAggregation: {
      enabled: false,
      select: "daily",
      options: [
        { id: "daily", description: "Daily Observations" },
        { id: "acc", description: "Accumulated" },
      ],
    },

    selectAggregationPeriod: {
      select: { id: "1D", description: "Daily" },
      options: [
        { id: "1D", description: "Daily" },
        { id: "7D", description: "Weekly" },
        { id: "1M", description: "Monthly" },
        { id: "1Y", description: "Yearly" },
      ],
    }, //, {id: 'flexible', description: 'Flexible'}] },

    selectYearsComparison: {
      select: [],
      options: [
        { id: "lta", description: "Long Term Average" },
      ],
    },

    compareYears: {
      select: [],
      options: [],
    },
    compareYearsData: {},
    compareYearsLoading: false,

    selectVariables: { select: [], previous: [], options: [] },

    // Consecutive Heat Index exposure — the condition the persistence metrics are
    // measured against. Defaults to the reference condition of the feature spec.
    hiExposure: {
        level: 'extremeCaution',  // HI >= 91 degF
        minHours: 6,              // N: minimum exposure hours per day
        minDays: 5,               // D: minimum consecutive days for an event
        levels: HI_EXPOSURE_LEVELS.map(level => ({
            value: level.id,
            text: `${level.label} (HI ≥ ${level.thresholdF}°F)`,
        })),
    },

    /*
     */
    selectDates: { begin: null, end: null },

    isShowHisComparison: false,

    variableBefore: "",
    mounted: false,

    // Was `null`. That let onTabChange's early-return-before-assignment bug
    // (see the comment there) leave selectedTab stuck at null, so the RISK
    // tab's `riskActiveTab === tabIndex` check could never become true after
    // a "drop pin then Load Data while sitting on RISK with no prior pin"
    // sequence. 0 is also strictly more truthful: it's the tab actually
    // shown on mount (Dashboard's own `== 0` guard already assumes this).
    selectedTab: 0,

    // 
    onLoadDataClick: false,

    latLonForm: {
      error: null,
      dirty: false,
      text: "",
    },

    rules: {
      required: (value) => !!value || "Required.",
      latlon: (v) => {

        const decimalRegex = /^-?\d+(\.\d+)?$/;

        if (!v) {
          return "Latitude and Longitude are required"
        }

        const parts = v.split(',').map(p => p.trim())

        if (parts.length !== 2) {
          return "Please enter exactly one comma separating latitude and longitude"
        }

        const [latStr, lonStr] = parts

        if (!decimalRegex.test(latStr) || !decimalRegex.test(lonStr)) {
          return "Latitude and Longitude must be valid decimal numbers"
        }

        const lat = parseFloat(latStr)
        const lon = parseFloat(lonStr)

        if (!isFinite(lat) || !isFinite(lon)) {
          return "Coordinates must be finite numbers"
        }

        if (lat < -90 || lat > 90) {
          return "Latitude must be between -90 and 90"
        }

        if (lon < -180 || lon > 180) {
          return "Longitude must be between -180 and 180"
        }

        // error.v = null // ✅ passed
      },
    },

  }),
  computed: {
    ...mapState({
      mapLatLon: (state) => state.mapLatLon,
      latLonInfo: (state) => state.latLonInfo,

      variable: (state) => state.menuSettings.variable,

      pixelData: (state) => state.pixelData,

      pixelStats: (state) => state.pixelStats,

      pixelSeasonSummaryData: (state) => state.pixelSeasonSummary,

      dataLoading: (state) => state.dataLoading,
      pendingLoads: (state) => state.pendingLoads,

      dataAnalysisStatus: (state) => state.dataAnalysisStatus,
      dataAnalysisFullScreen: (state) => state.dataAnalysisFullScreen,

      variableConfig: (state) => state.variables.variables,
      variableCategory: (state) => state.variables.categories,
    }),

    /*
     * Functions
     */

    // Yesterday on the LOCAL calendar: toISOString() is UTC, which in the evening
    // (US timezones) already reads as tomorrow, so it allowed picking today and the
    // store's "end is yesterday" check for the Heat Index forecast never matched.
    getEndDate() {
      return this.localISODate(-1);
    },

    /*
     * Loading data - preloader
     */
    // Pre-loader: Load Data / variable changes (dataLoading) or a tab loading
    // its own data (pendingLoads, see trackLoading in the store)
    getDataLoading() {
      return this.dataLoading || this.pendingLoads > 0;
    },

    getVariable() {
      if (this.variable.id == null || !this.mounted)
        return { id: null, description: "", threshold: "" };

      if (this.variable.id != this.variableBefore) {
        this.onLoadData();
      }
      if (this.variableConfig[this.variable.id] == undefined)
        return { ...this.variableConfig['totR'], ...{ id: 'totR', description: this.variableConfig['totR'].description, threshold: "" } };
      
      return { ...this.variableConfig[this.variable.id], ...this.variable };
      //return this.variableConfig[this.variable.id];
    },

    getMapLatLon() {
      if (this.mapLatLon.latitude == null || this.mapLatLon.longitude == null)
        return { latitude: null, longitude: null };

      // if (this.mapLatLon.latitude != this.lastPosition.latitude ||
      //     this.mapLon.longitude != this.lastPosition.longitude
      //   ) this.setLastPosition();

      return {
        latitude: this.mapLatLon.latitude.toFixed(4),
        longitude: this.mapLatLon.longitude.toFixed(4),
      };
    },

    /*
     * TABLE TAB
     */
    getTableHeader() {
      if (this.pixelData.length == 0) return [];

      let header = [];
      const keys = Object.keys(this.pixelData[0]);
      keys.forEach((key) => {
        if (this.selectVariables.select.indexOf(key) == -1 && key != "date")
          return;

        header.push({
          text: key == "date" ? "Date" : this.variableConfig[key].description,
          value: key,
        });
      });
      return header;
    },

    /*
     * CHART PROCESSING
     */

    // SUMMARY TAB
    getSeasonSummaryChartData() {
      if (this.pixelSeasonSummaryData.length == 0) return [];
      return [
        {
          x: this.pixelSeasonSummaryData.map((el) => el.date),
          y: this.pixelSeasonSummaryData.map((el) => el[this.variable.id]),
          type: "bar",
        },
      ];
    },

    getAnomalyChartData() {
      
      if (this.pixelSeasonSummaryData.length == 0) return [];

      // Calc average
      // let average =
      //   this.pixelSeasonSummaryData
      //     .map((el) => {
      //       return el[this.variable.id];
      //     })
      //     .reduce((a, b) => a + b) / this.pixelSeasonSummaryData.length;


      let charts = [];
      ['totR', 'minT', 'maxT'].forEach(item => {


        let average = this.pixelSeasonSummaryData
          .map((el) => {
            return el[item];
          })
          .reduce((a, b) => a + b) / this.pixelSeasonSummaryData.length;

        charts.push({
          mainTitle: this.variableConfig[item].description,
          id: item,
          layout: {
            showlegend: false,
            legend: {"orientation": "h"}
          },
          x: this.pixelSeasonSummaryData.map((el) => el.date),
          y: this.pixelSeasonSummaryData.map(
            (el) => el[item] - average
          ), // this.pixelData[0].y.map((a) => a - average),
          type: "bar",
          name: "Deviation",
          marker: {
            color: this.pixelSeasonSummaryData.map((el) =>
              el[item] - average < 0 ? "#d62728" : "#1D77B4"
            ),
          }
          
        })

      });
      return charts;

    },

    // Data analysis - show panel variables (such as dry spell or wet spell)
    // Where the variables can't be displayed in a graph, so it is displayed on panels
    getPanelData() {
      const inCompareMode = this.compareYears.select.length > 0;

      if (inCompareMode) {
        let panels = [];
        this.compareYears.select.forEach(year => {
          const yearData = this.compareYearsData[year];
          if (!yearData || yearData.length === 0) return;
          const items = this.computeSpellPanelItems(yearData);
          items.forEach(item => panels.push({ ...item, subtitle: String(year) }));
        });
        return panels;
      }

      if (this.pixelData.length == 0) return [];
      return this.computeSpellPanelItems(this.pixelData);
    },

    getHIData() {
      const HI_CATEGORIES = [
        { key: "hi_caution",        name: "Caution",         color: "#FFD54F" },
        { key: "hi_extremeCaution", name: "Extreme Caution", color: "#FB8C00" },
        { key: "hi_danger",         name: "Danger",          color: "#E53935" },
        { key: "hi_extremeDanger",  name: "Extreme Danger",  color: "#8E24AA" },
      ];
      const SUFFIX = "hours";

      // Nothing to plot when every category is zero/absent for the whole period
      const hasCategoryData = rows => rows.some(
        el => HI_CATEGORIES.some(({ key }) => (Number(el[key]) || 0) > 0)
      );

      if (this.selectVariables.select.indexOf('hi') == -1 || !hasCategoryData) return []

      // Builds the four stacked category traces for one data array, from the rows
      // the Heat Index model returned (not the days only another variable covers).
      const buildTraces = allRows => {
        const rows = allRows.filter(el => HI_CATEGORIES.some(({ key }) => el[key] !== undefined));
        return HI_CATEGORIES.map(({ key, name, color }) => ({
          x: rows.map(el => el.date),
          y: rows.map(el => el[key] ?? 0),
          yaxis: "y1",
          name,
          ySuffix: SUFFIX,
          line:   { color },
          marker: { color },
          type: "bar",
        }));
      };

      if (this.compareYears.select.length > 0) {
        const years = this.compareYears.select.filter(
          year => this.compareYearsData[year]
            && this.compareYearsData[year].length > 0
            && hasCategoryData(this.compareYearsData[year])
        );
        if (years.length === 0) return [];

        // Returns one chart per entry: { year, traces } — one chart per season, each on
        // its own real dates. The Accumulated toggle does not apply here: the running
        // total lives in the Daily Observation chart (getChartData).
        // Stacked bars: one chart per season, each on its own real dates
        return years.map(year => ({ year, traces: buildTraces(this.compareYearsData[year]) }));
      }

      return [{ year: null, traces: buildTraces(this.pixelData) }];
    },

    // Persistence of exposure: for how many CONSECUTIVE days did the daily exposure
    // condition hold. The detection itself lives in hiExposure.js (pure and unit
    // tested); this only feeds it the loaded records and the chosen condition.
    getHIExposure() {
      if (this.selectVariables.select.indexOf('hi') === -1) return null;
      if (!this.pixelData || this.pixelData.length === 0) return null;
      // Persistence is counted in CALENDAR DAYS, so it only means anything while the
      // records are daily. Under Weekly/Monthly aggregation each row spans several
      // days and consecutiveness cannot be read from it.
      if (this.selectAggregationPeriod.select.id !== '1D') return null;

      return summarizeExposure(this.pixelData, {
        level: this.hiExposure.level,
        minHours: Number(this.hiExposure.minHours),
        minDays: Number(this.hiExposure.minDays),
      });
    },

    // The same persistence measured on each compared season, so every season gets its
    // own copy of the chart below. Empty unless seasons are being compared.
    getHIExposureSeasons() {
      if (this.selectVariables.select.indexOf('hi') === -1) return [];
      if (this.selectAggregationPeriod.select.id !== '1D') return [];

      return this.compareYears.select
        .filter(year => this.compareYearsData[year] && this.compareYearsData[year].length > 0)
        .map(year => ({
          year,
          summary: summarizeExposure(this.compareYearsData[year], {
            level: this.hiExposure.level,
            minHours: Number(this.hiExposure.minHours),
            minDays: Number(this.hiExposure.minDays),
          }),
        }));
    },

    // Daily exposure hours, with the minimum (N) drawn as a reference line — the
    // qualifying days are the ones reaching it.
    getHIExposureChart() {
      return this.hiExposureChart(this.getHIExposure);
    },

    // Diurnal profile: how many events of each category happened at each hour
    // of the day over the whole period. One line per category.
    getHIHourlyData() {
      const HI_CATEGORIES = [
        { key: "caution",        name: "Caution",         color: "#FFD54F" },
        { key: "extremeCaution", name: "Extreme Caution", color: "#FB8C00" },
        { key: "danger",         name: "Danger",          color: "#E53935" },
        { key: "extremeDanger",  name: "Extreme Danger",  color: "#8E24AA" },
      ];
      // Years share the category colors, so they are told apart by line style
      const YEAR_DASHES = ['solid', 'dash', 'dot', 'dashdot', 'longdash'];
      const HOURS = Array.from({ length: 24 }, (unused, hour) => hour);

      // Every record carries the hours each category occurred on
      // ("hourly_summary"); tallying them across records gives the profile.
      const countByHour = rows => {
        const counts = {};
        HI_CATEGORIES.forEach(({ key }) => { counts[key] = new Array(24).fill(0); });

        rows.forEach(el => {
          const summary = el.hourly_summary;
          if (!summary) return;

          HI_CATEGORIES.forEach(({ key }) => {
            if (!Array.isArray(summary[key])) return;
            summary[key].forEach(hour => {
              if (hour >= 0 && hour < 24) counts[key][hour] += 1;
            });
          });
        });

        return counts;
      };

      const buildTraces = (rows, year, dash) => {
        const counts = countByHour(rows);
        if (!HI_CATEGORIES.some(({ key }) => counts[key].some(value => value > 0))) return [];

        return HI_CATEGORIES.map(({ key, name, color }) => ({
          x: HOURS,
          y: counts[key],
          yaxis: "y1",
          name: year ? `${name} (${year})` : name,
          ySuffix: "events",
          line:   { color, dash },
          marker: { color },
          type: "scatter",
          mode: "lines+markers",
        }));
      };

      if (this.compareYears.select.length > 0) {
        let chart = [];
        this.compareYears.select.forEach((year, yearIdx) => {
          const yearData = this.compareYearsData[year];
          if (!yearData || yearData.length === 0) return;
          chart = chart.concat(
            buildTraces(yearData, year, YEAR_DASHES[yearIdx % YEAR_DASHES.length])
          );
        });
        return chart;
      }

      if (this.pixelData.length == 0) return [];

      return buildTraces(this.pixelData, null, 'solid');
    },

    getHILayoutOverrides() {
      if (this.compareYears.select.length === 0) return {};
      // The accumulated view plots real positions, so the unified hover works.
      // The stacked bars sit off their real position, which would put a
      // shifted date in the unified hover title: hover each stack on its own.
      if (this.selectAggregation.enabled) return { xaxis: { tickformat: '%b %d' } };
      return { xaxis: { tickformat: '%b %d' }, hovermode: 'closest' };
    },

    // DATA ANALYSIS TAB
    getChartData() {
      const YEAR_COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'];
      const inCompareMode = this.compareYears.select.length > 0;

      if (this.pixelData.length == 0 && !inCompareMode) return [];

      let chart = [];

      // LTA and (when no compare mode) current period traces from pixelData
      if (this.pixelData.length > 0) {
        const keys = this.dataKeys(this.pixelData);
        let idx = 1;
        keys.forEach((key) => {
          if (this.selectVariables.select.indexOf(key) == -1 || key == "date")
            if (key.includes("His")) {
              if (this.selectVariables.select.indexOf(key.substring(0, key.length - 3)) == -1) { return; }
              if (this.selectYearsComparison.select.indexOf("lta") == -1) return;
            } else if (!key.includes("His")) return;

          // Non-"His" vars only appear via comparison year traces (or if "sp" is ever re-enabled).
          if (this.selectYearsComparison.select.indexOf("sp") == -1 && !key.includes("His")) return;

          if (this.variableConfig[key].viewMode.default == "panel") return;

          // Only the rows carrying this variable: rows merged in from another source
          // (e.g. the Heat Index forecast days) must not stretch its x axis.
          const rows = this.pixelData.filter((el) => el[key] !== undefined);

          let yValues = rows.map((el) => {
            const val = el[key];
            return val !== undefined && val !== null
              ? parseFloat(val).toFixed(this.variableConfig[key].round)
              : null;
          });

          if (this.selectAggregation.enabled) {
            if (!this.variableConfig[key].isShowAccumulate) return;
            yValues.reduce((a, b, i) => { return (yValues[i] = +a + +b); }, 0);
          }

          // pixelData covers the selected period, which is the axis itself
          chart.push({
            x: rows.map((el) => el.date),
            y: yValues,
            yaxis: this.variableConfig[key].chart.axis,
            name: this.variableConfig[key].description,
            range: this.variableConfig[key].chart.range,
            ySuffix: this.variableConfig[key].unit,
            layout: { title: this.variableConfig[key].description },
            line: { color: this.variableConfig[key].chart.line.color },
            marker: { color: this.variableConfig[key].chart.line.color },
            type: this.selectAggregation.enabled ? "scatter" : this.variableConfig[key].chart.default,
            mode: this.variableConfig[key].chart.mode,
            // CSV export: real dates. In compare mode the LTA belongs to no year,
            // so it is matched to every year's rows through their axis position.
            csvX: rows.map((el) => el.date),
            csvName: this.variableConfig[key].description,
            csvOnAxis: inCompareMode && key.includes("His"),
          });
          idx = idx + 1;
        });
      }

      // Comparison year traces
      const multiYear = this.compareYears.select.length > 1;
      this.compareYears.select.forEach((year, yearIdx) => {
        const yearData = this.compareYearsData[year];
        if (!yearData || yearData.length === 0) return;

        const yearColor = YEAR_COLORS[yearIdx % YEAR_COLORS.length];
        const yearKeys = this.dataKeys(yearData);

        yearKeys.forEach((key) => {
          if (key === 'date' || key.includes('His')) return;
          if (this.selectVariables.select.indexOf(key) === -1) return;
          if (!this.variableConfig[key]) return;
          if (this.variableConfig[key].viewMode.default === 'panel') return;

          // Only the rows carrying this variable (see the pixelData traces above)
          const rows = yearData.filter(el => el[key] !== undefined);

          let yValues = rows.map(el => {
            const val = el[key];
            return val !== undefined && val !== null
              ? parseFloat(val).toFixed(this.variableConfig[key].round)
              : null;
          });

          if (this.selectAggregation.enabled) {
            if (!this.variableConfig[key].isShowAccumulate) return;
            yValues.reduce((a, b, i) => { return (yValues[i] = +a + +b); }, 0);
          }

          const color = multiYear ? yearColor : this.variableConfig[key].chart.line.color;

          chart.push({
            x: rows.map(el => this.alignToSelectedPeriod(el.date, year)),
            y: yValues,
            yaxis: this.variableConfig[key].chart.axis,
            name: `${this.variableConfig[key].description} (${year})`,
            range: this.variableConfig[key].chart.range,
            ySuffix: this.variableConfig[key].unit,
            line: { color },
            marker: { color },
            type: this.selectAggregation.enabled ? 'scatter' : this.variableConfig[key].chart.default,
            mode: this.variableConfig[key].chart.mode,
            // CSV export: real dates, one column per variable shared by all years
            csvX: rows.map(el => el.date),
            csvName: this.variableConfig[key].description,
          });
        });
      });

      // Heat Index is counted per category, so the generic accumulate path above skips
      // it (isShowAccumulate: false — a temperature cannot be summed). With the toggle
      // on, plot the running total of event HOURS instead: the same numbers the daily
      // bars below show, added up.
      if (this.selectAggregation.enabled && this.selectVariables.select.indexOf('hi') !== -1) {
        const HI_KEYS = ['hi_caution', 'hi_extremeCaution', 'hi_danger', 'hi_extremeDanger'];
        const config = this.variableConfig['hi'];

        const accumulatedHiTrace = (allRows, year, color) => {
          const rows = allRows.filter(el => HI_KEYS.some(key => el[key] !== undefined));
          let running = 0;
          const y = rows.map(el => {
            running += HI_KEYS.reduce((sum, key) => sum + (Number(el[key]) || 0), 0);
            return running;
          });

          return {
            x: rows.map(el => (year ? this.alignToSelectedPeriod(el.date, year) : el.date)),
            y,
            // The variable's own axis is free here: `hi` itself (in °F) is never drawn
            // in accumulated mode, so hours never share an axis with temperatures.
            yaxis: config.chart.axis,
            name: year ? `Heat Index Events (${year})` : 'Heat Index Events',
            ySuffix: 'hours',
            line: { color },
            marker: { color },
            type: 'scatter',
            mode: 'lines',
            csvX: rows.map(el => el.date),
            csvName: 'Heat Index Events',
          };
        };

        if (inCompareMode) {
          this.compareYears.select.forEach((year, yearIdx) => {
            const yearData = this.compareYearsData[year];
            if (!yearData || yearData.length === 0) return;
            chart.push(accumulatedHiTrace(yearData, year, YEAR_COLORS[yearIdx % YEAR_COLORS.length]));
          });
        } else if (this.pixelData.length > 0) {
          chart.push(accumulatedHiTrace(this.pixelData, null, config.chart.line.color));
        }
      }

      return chart;
    },

    getChartLayoutOverrides() {
      if (this.compareYears.select.length === 0) return {};
      return { xaxis: { tickformat: '%b %d' } };
    },

    getSeasonStatistics() {
      let obj = {};
      let obj2 = {};
      //[this.pixelStats].forEach(item => {
      let keys = Object.keys(this.pixelStats);
      keys.forEach((key) => (obj[key] = this.pixelStats[key]));

      keys.forEach((key) => {
        const vals = this.pixelData.map((el) => el[key]);
        if (vals.length > 0)
          obj2[key] = {
            max: {
              [key]: Math.max(...vals),
            },
            min: {
              [key]: Math.min(...vals),
            },
            avg: {
              avg: this.variableConfig[key].isShowAccumulate
                ? vals.reduce((a, b) => a + b, 0)
                : vals.reduce((a, b) => a + b) / vals.length,
            },
          };
      });

      return {
        his: obj,
        current: obj2,
      };
    },
  },

  methods: {
    ...mapActions({
      getGeoInfo: 'getGeoInfo',
      getPixelData: "getPixelData",
      getModelData: "getModelData",
      getPixelSeasonSummary: "getPixelSeasonSummaryData",
      getPixelStats: "getPixelStats",
      setPixelData: "setPixelData",

      getPixelSeasonsSummaryDashboard: "getPixelSeasonsSummaryDashboard",

      setDataAnalysisStatus: "setDataAnalysisStatus",
      setDropPinStatus: "setDropPinStatus",
      setDataAnalysisFullScreen: "setDataAnalysisFullScreen",
      setDataLoading: "setDataLoading",
      trackLoading: "trackLoading",

      setAlert: "setAlert",

      setLatLon: "setLatLon",
      startNewDataRequest: "startNewDataRequest",
    }),

    buildCompareYearsOptions() {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const currentYear = today.getFullYear();

      if (!this.selectDates.begin || !this.selectDates.end) {
        const years = [];
        for (let year = currentYear; year >= 1981; year--) years.push(year);
        return years;
      }

      const beginParts = this.selectDates.begin.split('-');
      const endParts = this.selectDates.end.split('-');
      const yearDiff = parseInt(endParts[0]) - parseInt(beginParts[0]);

      const years = [];
      for (let year = currentYear; year >= 1981; year--) {
        const computedEnd = new Date(`${year + yearDiff}-${endParts[1]}-${endParts[2]}`);
        if (computedEnd <= today) years.push(year);
      }
      return years;
    },

    computeSpellPanelItems(data) {
      if (!data || data.length === 0) return [];
      const keys = Object.keys(data[0]);
      let values = [];

      keys.forEach((variable) => {
        if (this.selectVariables.select.indexOf(variable) === -1) return;
        if (!this.variableConfig[variable]) return;
        if (this.variableConfig[variable].viewMode.default !== 'panel') return;
        if (variable.includes('His')) return;

        let arr = [];
        let sub = 0;
        let begin = null;
        let end = null;

        data.forEach((item) => {
          const n = item[variable];
          if (n >= 1) {
            if (begin == null) begin = item['date'];
          } else {
            if (begin != null) arr.push({ begin, end, val: sub });
            sub = 0;
            begin = null;
          }
          end = item['date'];
          sub = n;
        });
        if (begin != null) arr.push({ begin, end, val: sub });

        if (arr.length === 0) return;

        const valMax = Math.max(...arr.map(i => i.val));
        const idxMax = arr.findIndex(i => i.val === valMax);
        const valueMax = { ...arr[idxMax], val: valMax.toString(), description: `(${arr[idxMax].begin} / ${arr[idxMax].end})` };

        const valMin = Math.min(...arr.map(i => i.val));
        const idxMin = arr.findIndex(i => i.val === valMin);
        const valueMin = { ...arr[idxMin], val: valMin.toString(), description: `(${arr[idxMin].begin} / ${arr[idxMin].end})` };

        if (variable === 'drySpell') {
          valueMin.val = '';
          valueMin.description = '';
        }

        values.push({
          min: valueMin,
          max: valueMax,
          avg: (arr.reduce((a, b) => ({ val: a.val + b.val })).val / data.length).toFixed(1),
          title: this.variableConfig[variable].description,
          icon: variable === 'wetSpell' ? 'mdi-weather-rainy' : 'mdi-weather-sunny-alert',
        });
      });

      return values;
    },

    checkLatLonForm() {
      // Validate this.latLonForm.text is "{lat},{lon}"
      if (this.latLonForm.text == null || this.latLonForm.text == "") return;
      if (this.latLonForm.text.indexOf(",") == -1) return;

      let split = this.latLonForm.text.split(",");
      if (split.length != 2) return;

      let lat = split[0].trim();
      let lon = split[1].trim();

      // Validate lat and lon with rules.latlon
      let error = this.rules.latlon(this.latLonForm.text);
      if (error != null) {
        this.latLonForm.error = error;
        return;
      }

      this.setLatLon({
        latitude: lat,
        longitude: lon,
      });
    },

    cleanLatLonForm() {
      this.latLonForm.error = null;
      this.latLonForm.dirty = false;
      this.latLonForm.text = `${this.lastPosition.latitude}, ${this.lastPosition.longitude}`;

      // Loose focus from the form
      this.$nextTick(() => {
        this.$refs.latLonInput.blur();
      });

      this.checkLatLonForm();

    },

    inputLatLonForm() {
      this.latLonForm.dirty = true;
      this.latLonForm.error = this.rules.latlon(this.latLonForm.text);
    },

    submitLatLonForm() {
      this.latLonForm.dirty = true;
      this.checkLatLonForm();

      this.onLoadData();
    },

    allowOnlyCharsLatLonForm(event) {
      const allowed = /[0-9.,-]/ // allow digits, dot, comma, minus
      const key = String.fromCharCode(event.charCode)
      if (!allowed.test(key)) {
        event.preventDefault()
      }
    },

    handlePasteLatLonForm(event) {
      const pasted = (event.clipboardData || window.clipboardData).getData('text')
      if (!/^[0-9.,\-\s]+$/.test(pasted)) {
        event.preventDefault()
      }
    },

    getIndicatorChartData(variable) {
      return [
        {
          domain: { x: [0.25, 1], y: [0, 1] },
          value: this.getSeasonStatistics.current[variable].avg.avg.toFixed(
            this.variableConfig[variable].round
          ),
          type: "indicator",
          mode: "number+gauge+delta",
          delta: {
            reference: this.getSeasonStatistics.his[variable].avg.avg.toFixed(
              this.variableConfig[variable].round
            ),
            relative: true,
          },
          gauge: {
            shape: "bullet",
            bordercolor: "#dddddd",
            bar: {
              color: "#01B3D9",
              thickness: 0.8,
            },
            axis: {
              visible: true,
              range: [
                this.getSeasonStatistics.his[variable].min[variable].toFixed(
                  this.variableConfig[variable].round
                ),
                this.getSeasonStatistics.his[variable].max[variable].toFixed(
                  this.variableConfig[variable].round
                ),
              ],
            },
            threshold: {
              line: { color: "#999", width: 2 },
              thickness: 0.9,
              value: this.getSeasonStatistics.his[variable].avg.avg.toFixed(
                this.variableConfig[variable].round
              ),
            },
          },
        },
      ];
    },

    setLastPosition() {
      this.lastPosition.latitude = this.mapLatLon.latitude;
      this.lastPosition.longitude = this.mapLatLon.longitude;
    },

    onTabChange: function (tab) {
      // Assignment moved ABOVE the no-pin guard. It used to sit after the
      // guard, so with no pin selectedTab never updated — sit on RISK with
      // no pin, drop one, hit Load Data (which calls
      // onTabChange(this.selectedTab)), and the guard now passes with the
      // STALE selectedTab, reassigning it back to itself instead of picking
      // up the tab the user is actually on. Harmless for tabs 0-2 (the only
      // consumer is Dashboard's `== 0` check), required for RISK's
      // `riskActiveTab === tabIndex` check to ever become true.
      this.selectedTab = tab;

      if (this.mapLatLon.latitude == null) return;

      if (tab == 1) {

        let vars = ["minT", "avgT", "maxT", "totR"].map((v) => {
          return {
            id: v,
            threshold: this.variableConfig[v].threshold.required ? this.variableConfig[v].threshold.default : null,
            type: this.variableConfig[v].type,
            dataObservation: this.variableConfig[v].dataObservation,
          };
        });
        
        // Load data
        //this.getPixelSeasonSummary({
        this.trackLoading(this.getPixelSeasonsSummaryDashboard({
          latitude: this.mapLatLon.latitude,
          longitude: this.mapLatLon.longitude,
          startDate: this.selectDates.begin,
          endDate: this.selectDates.end,
          variables: vars
          // variables: [{ ...this.variable, ...tp, ...interval, ...threshold }],
        }));
      }
    },

    // The RISK tab's "Change period" action (no-data state) emits
    // change-period; scroll/focus the Start Date field so the user's next
    // move is obvious rather than silently doing nothing.
    focusStartDate: function () {
      const field = this.$refs.startDateField;
      if (!field || !field.$el) return;
      field.$el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof field.focus === "function") field.focus();
    },

    /*
     * on add/remove variable from the v-select list
     */
    onVariableChange: async function (eventVariable) {
      this.setDataLoading(true);
      try {
        let diff = this.selectVariables.select.filter(
          (item) => this.selectVariables.previous.indexOf(item) < 0
        );

        // in case a threshold have been changed
        if (
          diff.length == 0 && typeof eventVariable === 'string' &&
          this.variableConfig[eventVariable] &&
          this.variableConfig[eventVariable].threshold.required
        ) {

          if (Array.isArray(diff))
            if (Array.isArray(eventVariable))
              diff.push(eventVariable[0])
            else
              diff = [eventVariable]; //.push(eventVariable);
        }

        if (diff.length > 0) {
          let vars = diff.map((v) => {
            return {
              id: v,
              threshold: this.variableConfig[v].threshold.required
                ? this.variableConfig[v].threshold.default
                : null,
              type: this.variableConfig[v].type,
              dataObservation: this.variableConfig[v].dataObservation,
            };
          });

          const params = {
            latitude: this.mapLatLon.latitude,
            longitude: this.mapLatLon.longitude,
            startDate: this.selectDates.begin,
            endDate: this.selectDates.end,
            agg: this.selectAggregationPeriod.select.id,
            variables: vars,
          };
          const results = await Promise.allSettled([
            // get pixel data
            this.getPixelData(params),
            // get season statistics
            this.getPixelStats(params),
          ]);

          const failed = results.some(r => r.status === 'rejected');
          if (failed) {
            this.setAlert({
              message: "Failed to load indicator data. Please try again.",
              type: "error",
            });
          }

          if (this.compareYears.select.length > 0) {
            this.compareYearsData = {};
            this.compareYearsLoading = true;
            try {
              for (const year of this.compareYears.select) {
                await this.fetchDataForYear(year);
              }
            } finally {
              this.compareYearsLoading = false;
            }
          }
        }
        this.selectVariables.previous = this.selectVariables.select;
      } catch (e) {
        console.error("onVariableChange error:", e);
        this.setAlert({
          message: "An unexpected error occurred while loading data. Please try again.",
          type: "error",
        });
      } finally {
        this.setDataLoading(false);
      }
    },

    /*
     * Data Analysis full screen mode (true or false)
     */
    expandDataAnalysis: function () {
      if (this.dataAnalysisStatus)
        this.setDataAnalysisFullScreen(!this.dataAnalysisFullScreen);
    },

    /*
     * Data Analysis Visibility (true or false). If false, the data analysis window will close and the drop pin turns is disabled.
     */
    closeDataAnalysis: function () {
      if (this.dataAnalysisFullScreen) this.setDataAnalysisFullScreen(false);
      this.setDataAnalysisStatus(false);
      this.setDropPinStatus(false);

      localStorage.removeItem('lastPin');
    },

    undoModifications: function() {
      this.setPixelData([]);
      // this.setAlert({ message: "Modifications undone. Please click on Load Data to refresh the data.", type: "info" });
      this.setLatLon({latitude: this.lastPosition.latitude, longitude: this.lastPosition.longitude});
      this.latLonForm.text = `${this.lastPosition.latitude}, ${this.lastPosition.longitude}`;
    },

    /*
     * onClick Load Data button
     */
    onLoadData: async function () {

      this.currentRequestId += 1;
      const myRequestId = this.currentRequestId;
      

      console.warn("fetching data")
      if (this.latLonForm.error != null) {
        this.setAlert({
          message: "Please enter a valid Latitude and Longitude",
          type: "error",
        });
        return;
      }
      this.setLastPosition();
      // Delete curr latLonInfo and get a fresh one
      this.$store.commit('SET_GEO_INFO', {})
      this.getGeoInfo({latitude: this.mapLatLon.latitude, longitude: this.mapLatLon.longitude});
      
      this.latLonForm.dirty = false;
      this.setDataLoading(true); 

      this.startNewDataRequest();

      this.onLoadDataClick = !this.onLoadDataClick;
      this.onTabChange(this.selectedTab);

      if (this.selectDates.begin == null || this.selectDates.end == null) {
        this.setAlert({
          message: "Please select a start and end date!",
          type: "error",
        });
        // It was switched on above; returning with it on left the panel locked
        this.setDataLoading(false);
        return;
      }

      // Rebuild valid year options based on the now-committed period
      this.compareYears.options = this.buildCompareYearsOptions();

      // Remove selected years that are no longer valid for the chosen period
      const invalidYears = this.compareYears.select.filter(y => !this.compareYears.options.includes(y));
      if (invalidYears.length > 0) {
        invalidYears.forEach(year => this.$delete(this.compareYearsData, year));
        this.compareYears.select = this.compareYears.select.filter(y => this.compareYears.options.includes(y));
        if (this.compareYears.select.length === 0 && this.compareYears.options.length > 0) {
          this.compareYears.select = [this.compareYears.options[0]];
        }
      }

      /* If the user have selected another variable on the left menu */
      if (this.variableBefore != this.variable.id) {
        if (this.selectVariables.select.length > 0) {
          if (this.selectVariables.select.indexOf(this.variable.id) == -1 && this.variableConfig[this.variable.id != undefined]) {
            this.selectVariables.select.push(this.variable.id);
          }
          this.selectVariables.previous = this.selectVariables.select;
        } else {
          this.selectVariables.select.push(this.variable.id);
        }
        this.variableBefore = this.variable.id;
      }

      // Sempre que clicar em load Data, o array state.pixelData deve ser limpo
      this.setPixelData([]);

      let vars = this.selectVariables.select.map((v) => {
        v = this.variableConfig[v] == undefined ? 'totR' : v;
        return {
          id: v,
          threshold: this.variableConfig[v].threshold.required
            ? this.variableConfig[v].threshold.default
            : null,
          type: this.variableConfig[v].type,
          dataObservation: this.variableConfig[v].dataObservation,

          // hist data
          hist: true,
        };
      });

      const params = {
        latitude: this.mapLatLon.latitude,
        longitude: this.mapLatLon.longitude,
        startDate: this.selectDates.begin,
        endDate: this.selectDates.end,
        agg: this.selectAggregationPeriod.select.id,
        variables: vars,
      };
      await Promise.allSettled([
      // get pixel data
      this.getPixelData(params),

      // get season statistics
      this.getPixelStats(params)]);


      // The compared seasons are the Indicators tab's main series, so the
      // pre-loader stays on until they arrive too. A newer Load Data owns it.
      if (this.currentRequestId === myRequestId) {
        try {
          if (this.compareYears.select.length > 0) {
            this.compareYearsData = {};
            this.compareYearsLoading = true;
            try {
              for (const year of this.compareYears.select) {
                await this.fetchDataForYear(year);
              }
            } finally {
              this.compareYearsLoading = false;
            }
          }
        } finally {
          if (this.currentRequestId === myRequestId) this.setDataLoading(false);
        }
      }
    },

    /*
     * Download CSV table
     */
    downloadCSV: function () {
      let csvContent = "data:text/csv;charset=utf-8,";

      let idx = Object.keys(this.pixelData[0]).indexOf("month_day");

      csvContent += [
        Object.keys(this.pixelData[0])
          .filter((e) => e !== "month_day")
          .join(","),
        ...this.pixelData.map((item) => {
          let vals = Object.values(item);
          vals.splice(idx, 1);
          return vals.join(",");
        }),
      ]
        .join("\n")
        .replace(/(^\[)|(\]$)/gm, "");

      // Work around to replace kdd variable id for sdd ()
      csvContent = csvContent.replace("kdd", "sdd");
      csvContent = csvContent.replace("kddHis", "sddHis");

      const data = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", "ClimateIndicators.csv");
      link.click();

      this.setAlert({ message: "CSV created successfully", type: "success" });
    },

    // Daily exposure hours for one summary, with the minimum (N) drawn as a reference
    // line — the qualifying days are the ones reaching it.
    hiExposureChart(summary) {
      if (!summary || summary.days.length === 0) return [];

      const FILL = { qualifies: "#E53935", no: "#C7CDD3", unknown: "#90A4AE" };
      const dates = summary.days.map(day => day.date);

      return [
        {
          x: dates,
          y: summary.days.map(day => day.hours),
          name: "Daily exposure hours",
          ySuffix: "hours",
          type: "bar",
          marker: { color: summary.days.map(day => FILL[day.status]) },
          customdata: summary.days.map(day => day.status),
          hovertemplate: "<b>%{x}</b><br>%{y} h ≥ threshold — %{customdata}<extra></extra>",
        },
        {
          x: dates,
          y: dates.map(() => summary.minimum_hours_per_day),
          name: `Minimum (${summary.minimum_hours_per_day} h/day)`,
          type: "scatter",
          mode: "lines",
          line: { color: "#455A64", width: 2, dash: "dash" },
          // Explicit, so Chart.vue leaves this trace's hover alone instead of
          // attaching the generic one it gives every other series.
          hovertemplate: "Minimum: %{y} h/day<extra></extra>",
        },
      ];
    },

    // Places a record of the season starting in `seasonYear` on the selected
    // period's real dates, keeping its month/day (see compareAxis.js).
    alignToSelectedPeriod(dateStr, seasonYear) {
      if (!this.selectDates.begin) return dateStr;
      const referenceYear = parseInt(this.selectDates.begin.slice(0, 4), 10);
      return alignToReferenceYear(dateStr, seasonYear, referenceYear);
    },

    // Smallest positive gap between the given timestamps (ms), used to size
    // the bar slot of each aggregation period. Falls back to one day.
    smallestStep(timestamps) {
      const DAY = 24 * 60 * 60 * 1000;
      const unique = [...new Set(timestamps.filter(ms => !isNaN(ms)))].sort((a, b) => a - b);
      if (unique.length < 2) return DAY;

      let step = Infinity;
      for (let i = 1; i < unique.length; i++) {
        const diff = unique[i] - unique[i - 1];
        if (diff > 0 && diff < step) step = diff;
      }
      return step === Infinity ? DAY : step;
    },

    // "YYYY-MM-DD HH:mm:ss" in UTC, the format Plotly parses unambiguously
    formatPlotlyDate(ms) {
      const d = new Date(ms);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
        `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    },

    // Every key present in any row: rows merged from several sources (e.g. the
    // Heat Index forecast days) do not all carry the same variables.
    dataKeys(rows) {
      const keys = new Set();
      rows.forEach(row => Object.keys(row).forEach(key => keys.add(key)));
      return [...keys];
    },

    mergeDataArrays(arrays) {
      if (!arrays || arrays.length === 0) return [];
      const map = new Map();
      arrays.forEach(arr => {
        if (!Array.isArray(arr)) return;
        arr.forEach(item => {
          if (map.has(item.date)) {
            Object.assign(map.get(item.date), item);
          } else {
            map.set(item.date, { ...item });
          }
        });
      });
      return Array.from(map.values()).sort((a, b) => a.date < b.date ? -1 : 1);
    },

    async fetchDataForYear(year) {
      if (!this.selectDates.begin || !this.selectDates.end) return;
      if (!this.mapLatLon.latitude || !this.mapLatLon.longitude) return;

      const beginParts = this.selectDates.begin.split('-');
      var endParts = this.selectDates.end.split('-');
      const yearDiff = parseInt(endParts[0]) - parseInt(beginParts[0]);
      const startDate = `${year}-${beginParts[1]}-${beginParts[2]}`;
      
      /*
        Considering that the selected date is yesterday (latest available data,
        I'll add 5 more days for forecast observation)
      */
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.getFullYear() + '-' +
        String(yesterday.getMonth() + 1).padStart(2, '0') + '-' +
        String(yesterday.getDate()).padStart(2, '0');

      var tmpEndDate = this.selectDates.end;

      if (tmpEndDate === yesterdayStr) {
        const d = new Date(tmpEndDate + 'T23:00:00');
        tmpEndDate = d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
      }

      endParts = tmpEndDate.split('-');
      
      const endDate = `${year + yearDiff}-${endParts[1]}-${endParts[2]}`;

      const nonModelVars = this.selectVariables.select
        .filter(v => this.variableConfig[v] && this.variableConfig[v].type !== 'model')
        .map(v => ({
          id: v,
          threshold: this.variableConfig[v].threshold.required
            ? this.variableConfig[v].threshold.default
            : null,
          type: this.variableConfig[v].type,
          dataObservation: this.variableConfig[v].dataObservation,
        }));

      const modelVars = this.selectVariables.select
        .filter(v => this.variableConfig[v] && this.variableConfig[v].type === 'model')
        .map(v => ({
          id: v,
          threshold: this.variableConfig[v].threshold.required
            ? this.variableConfig[v].threshold.default
            : null,
          type: this.variableConfig[v].type,
          dataObservation: this.variableConfig[v].dataObservation,
        }));

      if (nonModelVars.length === 0 && modelVars.length === 0) return;

      try {
        let rawArrays = [];

        if (nonModelVars.length > 0) {
          const pixelRaw = await this.getPixelData({
            latitude: this.mapLatLon.latitude,
            longitude: this.mapLatLon.longitude,
            startDate,
            endDate,
            agg: this.selectAggregationPeriod.select.id,
            variables: nonModelVars,
            raw: true,
          });
          if (Array.isArray(pixelRaw)) rawArrays = rawArrays.concat(pixelRaw);
        }

        if (modelVars.length > 0) {
          const modelRaw = await this.getModelData({
            latitude: this.mapLatLon.latitude,
            longitude: this.mapLatLon.longitude,
            startDate,
            endDate,
            agg: this.selectAggregationPeriod.select.id,
            variables: modelVars,
            raw: true,
          });
          if (Array.isArray(modelRaw)) rawArrays = rawArrays.concat(modelRaw.filter(Boolean));
        }

        // Keep only the requested season: a source answering with other dates must
        // not leak them into the chart. A season reaching the latest available day
        // also keeps the forecast days the store requests (up to today + 5).
        const windowEnd = endDate >= this.localISODate(-1) ? this.localISODate(5) : endDate;
        const merged = this.mergeDataArrays(rawArrays).filter(row => {
          const day = String(row.date).slice(0, 10);
          return day >= startDate && day <= windowEnd;
        });
        this.$set(this.compareYearsData, year, merged);
      } catch (e) {
        if (e && e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
          console.error(`Error fetching data for year ${year}:`, e);
          this.setAlert({ message: `Failed to load data for ${year}. Please try again.`, type: 'error' });
        }
      }
    },

    async onCompareYearsChange(newSelection) {
      if (newSelection.length > 5) {
        this.compareYears.select = newSelection.slice(0, 5);
        this.setAlert({ message: 'You can compare up to 5 years at a time.', type: 'warning' });
        return;
      }

      // Remove data for deselected years
      Object.keys(this.compareYearsData).forEach(year => {
        if (newSelection.indexOf(parseInt(year)) === -1) {
          this.$delete(this.compareYearsData, year);
        }
      });

      // Fetch data for newly selected years
      const toFetch = newSelection.filter(year => !this.compareYearsData[year]);
      if (toFetch.length > 0) {
        this.compareYearsLoading = true;
        try {
          await this.trackLoading((async () => {
            for (const year of toFetch) {
              await this.fetchDataForYear(year);
            }
          })());
        } finally {
          this.compareYearsLoading = false;
        }
      }
    },

    // "YYYY-MM-DD" of today + `offsetDays`, on the local calendar
    localISODate(offsetDays = 0) {
      const d = new Date();
      d.setDate(d.getDate() + offsetDays);
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
    },

    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },

    exportChartAsImage(chartRefName, filename) {
      this.loadingCharts = {...this.loadingCharts, [chartRefName]: true };
      setTimeout(() => {
        try {
          // const chartElement = this.$refs[chartRefName];
          const chartElement = Array.isArray(this.$refs[chartRefName]) ? this.$refs[chartRefName][0] : this.$refs[chartRefName];

          if (!chartElement) {
            console.error(`Chart with ref "${chartRefName}" not found.`);
            this.loadingCharts = {...this.loadingCharts, [chartRefName]: false };
            return;
          }

          // Options for html2canvas
          const options = {
            useCORS: true,
            allowTaint: true,
            scale: window.devicePixelRatio || 1, // Use device pixel ratio for better resolution
            // Ensure scroll position doesn't affect capture
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
          };

          html2canvas(chartElement.$el || chartElement, options)
            .then((chartCanvas) => {
              // 1. Create an in-memory image from the chart canvas
              const chartImage = new Image();
              chartImage.onload = () => {
                // 2. Load the logo
                const logo = new Image();
                logo.src = "https://cloud.agroclimate.org/tools/climate-indicators-monitoring/agroclimate_main_logo.png";
                logo.crossOrigin = "anonymous";

                logo.onload = () => {
                  // 3. Create a new canvas to combine chart and logo
                  const finalCanvas = document.createElement("canvas");
                  finalCanvas.width = chartCanvas.width;
                  finalCanvas.height = chartCanvas.height;
                  const ctx = finalCanvas.getContext("2d");

                  // 4. Draw the captured chart image onto the new canvas
                  ctx.drawImage(chartImage, 0, 0);

                  // 5. Calculate logo position on the new canvas (bottom-right)
                  const padding = 20 * options.scale; // Scale padding
                  const logoWidth = 30 * options.scale; // Scale logo size
                  const logoHeight = (logo.height / logo.width) * logoWidth;
                  const x = finalCanvas.width - logoWidth - padding;
                  const y = padding;

                  // 6. Draw the logo onto the new canvas
                  ctx.drawImage(logo, x, y, logoWidth, logoHeight);

                  // 7. Generate the final image and trigger download
                  try {
                    const link = document.createElement("a");
                    link.download = `${filename || chartRefName}.png`;
                    link.href = finalCanvas.toDataURL("image/png");
                    link.click();
                  } catch (err) {
                    console.error("Error creating download link:", err);
                  } finally {
                    // this.loadingCharts[chartRefName] = false;
                    this.loadingCharts = {...this.loadingCharts, [chartRefName]: false };
                  }
                };

                logo.onerror = (err) => {
                  console.error("Error loading logo:", err);
                  // Proceed to download chart without logo if logo fails
                  try {
                    const link = document.createElement("a");
                    link.download = `${filename || chartRefName}.png`;
                    // Use the original chart canvas data URL
                    link.href = chartCanvas.toDataURL("image/png");
                    link.click();
                  } catch (linkErr) {
                    console.error(
                      "Error creating download link (no logo):",
                      linkErr
                    );
                  } finally {
                    // this.loadingCharts[chartRefName] = false;
                    this.loadingCharts = {};
                  }
                };
              };

              chartImage.onerror = (err) => {
                console.error("Error loading chart canvas into image:", err);
                this.loadingCharts = {...this.loadingCharts, [chartRefName]: false };
                // this.loadingCharts[chartRefName] = false;
                // this.loadingCharts = {};
              };

              // Set the source for the chart image *after* defining onload
              chartImage.src = chartCanvas.toDataURL("image/png");
            })
            .catch((err) => {
              console.error("Error generating canvas with html2canvas:", err);
              this.loadingCharts = {...this.loadingCharts, [chartRefName]: false };
              // this.loadingCharts[chartRefName] = false;
              // this.loadingCharts = {};
            });
        } catch (error) {
          console.error("Error exporting chart as image:", error);
          this.loadingCharts = {...this.loadingCharts, [chartRefName]: false };
          // this.loadingCharts[chartRefName] = false;
          // this.loadingCharts = {};
        }
      }, 200);
    },

    exportChartAsCSV(chartRefName, filename) {
      const chartData = typeof(chartRefName) == 'string' ? this[chartRefName] : chartRefName;

      if (!downloadChartCsv(chartData, filename)) {
        console.error(`Chart with ref "${chartRefName}" has no data.`);
      }
    },

    annualChartCsvFilename(item) {
      return annualChartCsvFilename(item.id, this.variableConfig[item.id]);
    },
  },

  mounted() {
    // Populate comparison years select
    //for (let year = new Date().getFullYear() - 1 ; year > 1981 ; year--)
    //  this.selectYearsComparison.options.push({id: year, description: year});

    // Open with the menu variable selected
    // this variable will not be allowed to unselect

    
    if (this.variableConfig[this.variable.id] == undefined) {
      this.selectVariables.select.push('totR');
      this.selectVariables.previous.push('totR');
    } else {
      this.selectVariables.select.push(this.variable.id);
      this.selectVariables.previous.push(this.variable.id);
    }

    // mount variables list based on variableConfig object
    let vars = Object.keys(this.variableConfig);

    vars.forEach((variable) => {
      if (variable.includes("His")) return;

      this.selectVariables.options.push({
        id: variable,
        description: this.variableConfig[variable].description,
        // description: this.variableConfig[variable].isShowThreshold
        //   ? this.variableConfig[variable].description +
        //     " " +
        //     this.variableConfig[variable].threshold.default +
        //     "°F"
        //   : this.variableConfig[variable].description,
        category: this.variableConfig[variable].category,
        disable: this.variable.id == variable ? true : false,
      });
    });

    if (this.selectDates.begin == null) {
      this.selectDates.begin = this.localISODate(-30);
      // Latest available day, so the Heat Index forecast days load with it
      this.selectDates.end = this.localISODate(-1);
    }

    // Build valid year options based on default dates
    this.compareYears.options = this.buildCompareYearsOptions();

    // Pre-select the year of the loaded period; fall back to the most recent valid year
    const preSelectedYear = parseInt(this.selectDates.begin.split('-')[0]);
    if (this.compareYears.options.includes(preSelectedYear)) {
      this.compareYears.select = [preSelectedYear];
    } else if (this.compareYears.options.length > 0) {
      this.compareYears.select = [this.compareYears.options[0]];
    }

    // Load last 30 days
    this.mounted = true;
    this.onLoadData();
  },

  watch: {
    getMapLatLon: function (newVal, oldVal) {
      if (this.latLonForm.text && (newVal.latitude == null || newVal.longitude == null)) return;
      if (this.latLonForm.text && (oldVal.latitude == newVal.latitude && oldVal.longitude == newVal.longitude)) return;

      this.latLonForm.text = `${newVal.latitude}, ${newVal.longitude}`;

    },
  }
};
</script>

<style lang="scss" src="./DataAnalysis.scss">
</style>